import { useState, type ChangeEvent } from 'react';
import {
  Alert, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth/AuthContext';
import { useSzkolyRestauracji } from '@/lib/useAnalytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PANEL RESTAURACJI → SZKOŁY. TYLKO DO ODCZYTU.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ TO JEST ŚWIADOME ODEJŚCIE OD STAREGO PANELU ───────────────────────────────────────────────┐
 * │ Stary panel pozwalał restauracji SAMODZIELNIE dopisywać sobie szkoły                        │
 * │ (`POST /api/Restaurant/current/school/{id}`). Właściciel przeniósł tę zdolność do           │
 * │ Administracja → Organizacja.                                                                 │
 * │                                                                                              │
 * │ Nie jest to brak parytetu, tylko decyzja: relacja szkoła–restauracja jest ustaleniem        │
 * │ organizacyjnym, a nie czymś, co jedna strona nadaje sobie sama.                             │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Ekran nie ma i nie będzie miał żadnego sterowania zapisem. Zabezpieczeniem jest to, że
 * trasa serwera nie wystawia metody zapisu — brak przycisku sam z siebie niczego nie chroni.
 */

const DNI = ['pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd'];
const DNI_PELNE = ['poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota', 'niedziela'];

export function SzkolyRestauracjiPage() {
  const auth = useAuth();
  const czyWlasciciel = auth.has('admin.users.read');
  const [restauracja, setRestauracja] = useState('');

  const wskazana = restauracja.trim() === '' ? undefined : Number(restauracja);
  const { data, isLoading, error } = useSzkolyRestauracji(wskazana);

  const brakWskazania = czyWlasciciel && wskazana === undefined;
  const szkoly = data?.szkoly ?? [];

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Szkoły"
        opis="Szkoły obsługiwane przez tę restaurację, dni dostaw i godzina zamknięcia zamówień."
      />

      <Alert severity="info">
        <strong>Widok tylko do odczytu.</strong> Przypisania szkół i dni dostaw zmienia się
        w <strong>Administracja → Organizacja</strong>. Restauracja nie nadaje sobie szkół sama —
        to ustalenie organizacyjne.
      </Alert>

      {czyWlasciciel && (
        <Karta>
          <Stack direction="row" spacing={2} sx={{ p: 2, alignItems: 'center' }}>
            <TextField label="Restauracja (nr)" size="small" sx={{ width: 180 }} value={restauracja}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRestauracja(e.target.value)}
              helperText="Konto właściciela nie ma własnej restauracji" />
          </Stack>
        </Karta>
      )}

      {brakWskazania && (
        <EmptyState
          title="Wskaż restaurację"
          description="Konto właściciela nie jest przypisane do restauracji, więc trzeba podać, czyj panel oglądasz."
        />
      )}

      {!brakWskazania && isLoading && <LoadingState />}
      {!brakWskazania && error ? <ErrorState error={error} context="Panel restauracji — szkoły." /> : null}

      {!brakWskazania && !isLoading && !error && szkoly.length === 0 && (
        <EmptyState
          title="Brak przypisanych szkół"
          description="Ta restauracja nie ma dziś żadnej szkoły w grafiku. Przypisania nadaje się w Administracja → Organizacja."
        />
      )}

      {szkoly.length > 0 && (
        <Karta>
          <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Szkoły obsługiwane przez restaurację">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Szkoła</TableCell>
                  {DNI.map((d, i) => (
                    <TableCell key={d} align="center" sx={{ fontWeight: 600 }}>
                      <Tooltip title={DNI_PELNE[i]}><span>{d}</span></Tooltip>
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Zamówienia do</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {szkoly.map((s) => (
                  <TableRow key={s.szkola_id} hover>
                    <TableCell>{s.szkola}</TableCell>
                    {DNI.map((_, i) => (
                      <TableCell key={i} align="center">
                        {s.dni_aktywne.includes(i)
                          ? <Chip size="small" label="✓" sx={{ minWidth: 32 }} />
                          : <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>}
                      </TableCell>
                    ))}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {/* Godzina różna w różne dni NIE jest zwijana do jednej — patrz Grafik. */}
                      {s.godzina_jednolita
                        ? (s.godzina ?? <Typography component="span" variant="body2"
                            sx={{ color: 'text.disabled' }}>nieznana</Typography>)
                        : <Chip size="small" color="warning" variant="outlined" label="różna w różne dni" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Karta>
      )}
    </Stack>
  );
}

export default SzkolyRestauracjiPage;
